import React, { useEffect, useState } from "react";
import { Copy, Loader2, Undo2 } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, errMsg, fmtDate } from "@/lib/api";

export function DuplicatesButton({ onChanged }: { onChanged: () => void }) {
  const [open, setOpen] = useState(false);
  const [d, setD] = useState<any>(null);
  const [busy, setBusy] = useState(false);
  const load = () => { setD(null); api.get("/dow/clearances/duplicates").then((r) => setD(r.data)).catch((e) => { toast.error(errMsg(e)); setOpen(false); }); };
  useEffect(() => { if (open) { load(); } }, [open]);
  const run = async (url: string, msg: (r: any) => string) => {
    setBusy(true);
    try { const { data } = await api.post(url, {}, { timeout: 600000 }); toast.success(msg(data)); onChanged(); load(); } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  return (
    <>
      <button className="btn-secondary btn-sm" onClick={() => setOpen(true)} data-testid="dow-dups-open"><Copy className="h-4 w-4" /> Find duplicates</button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="flex max-h-[90vh] max-w-2xl flex-col" data-testid="dow-dups-dialog">
          <DialogHeader><DialogTitle>Duplicate clearances</DialogTitle></DialogHeader>
          {!d ? <div className="py-10 text-center"><Loader2 className="mx-auto h-6 w-6 animate-spin text-[#94A3B8]" /><p className="mt-2 text-xs text-[#64748B]">Checking every clearance…</p></div> : (
            <div className="flex min-h-0 flex-1 flex-col gap-3">
              <p className="text-sm text-[#475569]">A duplicate is the same person (same name, in any order) with the same date of birth and the same clearance date. Different clearance dates are kept, since those are renewals.</p>
              <div className="rounded-xl bg-[#F8FAFC] p-3 text-sm" data-testid="dow-dups-summary">
                {d.extra ? <><b>{d.extra.toLocaleString()}</b> extra copies found for <b>{d.groups.toLocaleString()}</b> people. Merging keeps one record each (the most complete one), copies over any address, email or other details the others had, and removes the extras.</> : <b>No duplicates found.</b>}
              </div>
              {d.examples.length > 0 && (
                <div className="min-h-0 flex-1 overflow-auto rounded-lg border border-slate-100 text-xs" data-testid="dow-dups-examples">
                  <table className="tbl tbl-compact"><thead><tr><th>Name</th><th>DOB</th><th>Cleared</th><th>Parish / school</th><th>Address</th><th>Certificate #</th></tr></thead>
                    <tbody>{d.examples.map((g: any[], i: number) => g.map((c, j) => (
                      <tr key={`${i}-${j}`} className={j === 0 ? "border-t-2 border-slate-200" : "text-[#94A3B8]"}>
                        <td className="font-semibold">{c.name}{j === 0 && <span className="ml-1 rounded bg-[#DCFCE7] px-1 text-[10px] text-[#166534]">kept</span>}</td>
                        <td className="font-mono">{c.dob || "\u2014"}</td><td className="font-mono">{c.issued_at ? fmtDate(c.issued_at) : ""}</td>
                        <td>{c.location || "\u2014"}</td><td>{c.address || "\u2014"}</td><td className="font-mono">{c.number}</td>
                      </tr>
                    )))}</tbody>
                  </table>
                </div>
              )}
              {d.last_merge && (
                <div className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-slate-200 p-2.5 text-xs text-[#475569]" data-testid="dow-dups-last">
                  <span>Last merge: {d.last_merge.removed.toLocaleString()} removed {fmtDate(d.last_merge.at)}{d.last_merge.by_name ? ` by ${d.last_merge.by_name}` : ""}</span>
                  <button className="btn-secondary btn-sm" disabled={busy} onClick={() => run(`/dow/clearances/duplicates/undo/${d.last_merge.id}`, (r) => `Undone: ${r.restored.toLocaleString()} records put back`)} data-testid="dow-dups-undo"><Undo2 className="h-4 w-4" /> Undo that merge</button>
                </div>
              )}
              <div className="flex justify-end gap-2">
                <button className="btn-secondary btn-sm" onClick={() => setOpen(false)} data-testid="dow-dups-close">Close</button>
                {d.extra > 0 && <button className="btn-primary btn-sm" disabled={busy} onClick={() => run("/dow/clearances/duplicates/merge", (r) => `Merged: ${r.removed.toLocaleString()} duplicates removed`)} data-testid="dow-dups-merge">
                  {busy && <Loader2 className="h-4 w-4 animate-spin" />} Merge {d.extra.toLocaleString()} duplicates</button>}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
